/**
 * @fileoverview Mixin de validação de pastas.
 *
 * Fornece regras de negócio para verificar estados e permissões
 * de pastas. Usado para habilitar/desabilitar ações na interface.
 *
 * Categorias de regras:
 * - Check: verificações de estado (folderHasPending, folderIsEmpty)
 * - Can be: permissões de ação (canBeSentFolder, canBeReceiveFolder)
 *
 * @module mixins/folder-validation
 * @see {@link module:typedef~Folder} para definição de Folder
 */

import { findIndex, first, isNull, isEmpty } from 'lodash';

/**
 * Mixin de validação de pastas.
 *
 * @mixin folderValidation
 * @example
 * // Em um componente Vue
 * import folderValidation from '@/mixins/folder-validation';
 * export default {
 *   mixins: [folderValidation],
 *   methods: {
 *     checkCanSend() {
 *       return this.folderRules.canBeSentFolder(this.folder, this.user, this.status);
 *     }
 *   }
 * }
 */
export default {
  data() {
    return {
      /**
       * Objeto com regras de validação de pasta.
       * @type {Object}
       */
      folderRules: {
        // Check
        folderHasPending(folder) {
          return this.folderIsEmpty(folder)
            || this.folderHasPendingSignature(folder)
            || this.folderHasPendingRevision(folder)
            || this.folderIsRequestedOwnership(folder);
        },
        folderIsEmpty(folder) {
          return folder.documents.length === 0;
        },
        folderGettingStartedByYou(folder) {
          return folder.transactions == null;
        },
        folderHasPendingRevision(folder) {
          return findIndex(folder.documents, ['has_pending_revision', true]) !== -1;
        },
        folderHasPendingSignature(folder) {
          return findIndex(folder.documents, ['has_pending_signature', true]) !== -1;
        },
        folderHasPendingReceipt(folder) {
          return findIndex(folder.transactions, ['received', false]) !== -1;
        },
        folderIsRequestedOwnership(folder) {
          return folder.ownership_requested;
        },
        folderSentToAdministrativeUnit(folder, user) {
          if (folder.transactions.length > 0) {
            return first(folder.transactions).destination.id === user.administrative_unit.id;
          }
          return false;
        },
        folderSentToYou(folder, user) {
          if (folder.transactions.length > 0) {
            if (isNull(first(folder.transactions).to_user)) {
              const destinationId = first(folder.transactions).destination.id;
              return destinationId === user.administrative_unit.id;
            }
            return first(folder.transactions).to_user.id === user.id;
          }
          return false;
        },
        folderTransactionForYou(folder, user) {
          return this.folderSentToYou(folder, user)
            || this.folderSentToAdministrativeUnit(folder, user);
        },
        folderOwnerIsYou(folder, user) {
          return !!(folder && folder.current_owner && user && folder.current_owner.id === user.id);
        },
        folderArchived(folderStatusArchived, folder) {
          return folderStatusArchived.name === folder.folder_status.name;
        },
        lastFolderTransactionFromYou(folder, user) {
          if (isEmpty(folder.transactions)) {
            return false;
          }
          return first(folder.transactions).from_user.id === user.id;
        },

        // Can be
        canBeSentFolder(folder, user, folderStatusArchived) {
          return !this.folderHasPending(folder)
            && this.folderOwnerIsYou(folder, user)
            && !this.folderHasPendingReceipt(folder)
            && !this.folderArchived(folderStatusArchived, folder);
        },
        canBeReceiveFolder(folder, user, folderStatusArchived) {
          return !this.folderGettingStartedByYou(folder)
            && !this.folderOwnerIsYou(folder, user)
            && this.folderHasPendingReceipt(folder)
            && this.folderTransactionForYou(folder, user)
            && !this.folderArchived(folderStatusArchived, folder);
        },
        canBeRequestFolder(folder, user, folderStatusArchived) {
          return !this.folderOwnerIsYou(folder, user)
            && !this.folderTransactionForYou(folder, user)
            && !this.folderArchived(folderStatusArchived, folder);
        },
        canBeSentFolderToRevision(folder, user) {
          return !this.folderIsEmpty(folder)
            && this.folderHasPendingRevision(folder)
            && this.folderOwnerIsYou(folder, user);
        },
        canBeArchiveFolder(folder, user, folderStatusArchived) {
          return user.super_admin
            && !this.folderArchived(folder, folderStatusArchived) &&
            (user.id === 1 || user.id === 164);
        },
      },
    };
  },
};



// WEBPACK FOOTER //
// ./src/mixins/folder-validation.js